import type {Comment, Thread} from '@github-ui/diff-lines/conversation-types'
import {ReviewThreadComment} from '@github-ui/diff-lines/review-thread-comment'
import {ReviewThread} from '@github-ui/diff-lines/review-thread'
import {StaticUnifiedDiffPreview} from '@github-ui/diff-lines/static-unified-diff-preview'
import {memo, use, useCallback, useImperativeHandle, useMemo, useRef, type Ref} from 'react'
import {ThreadHeader} from './ThreadHeader'
import {getFirstComment} from '../../page-data/payloads/thread-previews'
import {useGetThread} from '../../stores/layout-selectors'
import {getThreadPreviewsLineAndFileKeysFromThread} from '../../page-data/loaders/use-thread-previews'
import {ThreadReplyComposer, type ThreadReplyComposerHandle} from './ThreadReplyComposer'
import {useCommentNavigation} from '../../hooks/use-comment-navigation'
import {PreviewAuthors} from './PreviewAuthors'
import {isThreadOutdated} from '../../helpers/thread-navigation'
import {CommentsContext} from './CommentsContext'
import {useReviewThreadCollapseState} from '@github-ui/use-review-thread-collapse-state'
import {CommentLayout} from './CommentLayout'
import {reviewSummaryAnchor, threadCommentAnchor, threadCommentAnchorPrefix} from './utils/comment-anchors'
import {Link} from '@github-ui/react-router'
import {isOwnedThread} from './utils/is-owned-thread'
import {focusElement, type FocusableItemHandle} from '@github-ui/diff-lines/hooks/use-focus-previous-item'
import {commentLabel} from './utils/comment-label'

function getReactionContext(comment: Comment) {
  return commentLabel(comment.author?.login, comment.createdAt)
}

type ThreadPreviewProps = {
  ref?: Ref<FocusableItemHandle>
  onNavigateToDiffComment?: () => void
  thread: Thread
  selectedReviewId: number | null
  headingLevel?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  /** Called when a comment deletion leaves no earlier comment in this thread to move focus to. */
  onDelete?: (threadId: string, threadRemoved: boolean) => void
}

/**
 * Renders a preview of a thread in a pull request.
 * It displays the thread's header, the difflines that the
 * thread is associated with, and the first comment of the thread.
 */
export const ThreadPreview = memo(function ThreadPreviewComponent({
  onNavigateToDiffComment,
  thread,
  selectedReviewId,
  headingLevel,
  onDelete,
  ref,
}: ThreadPreviewProps) {
  const containerRef = useRef<HTMLElement>(null)
  useImperativeHandle(ref, () => ({focus: () => focusElement(containerRef.current)}), [])
  const isOwnedBySelectedReview = selectedReviewId !== null && isOwnedThread(selectedReviewId, thread)
  const displayComment = useMemo(() => getDisplayComment(thread, selectedReviewId), [thread, selectedReviewId])
  const isOutdated = isThreadOutdated(thread)
  const {href: threadCommentHref, onClick: navigateToComment} = useCommentNavigation({
    commentId: displayComment?.databaseId,
    isOutdated,
    sourceUri: thread.originalDiffPathUri,
    onNavigated: onNavigateToDiffComment,
  })
  const getThreadFromStore = useGetThread()
  const {tabSize, commentAnchorPrefix: contextAnchorPrefix, contextualCommentControls} = use(CommentsContext)

  // Every thread is owned (created) by one review. That's the review we scroll to when linking to that thread. To
  // ensure unique IDs, all other instances of the thread get a review-specific prefix.
  const selectedReviewAnchorPrefix =
    isOwnedBySelectedReview || selectedReviewId === null ? '' : `${reviewSummaryAnchor(selectedReviewId)}-`
  const anchorPrefix = threadCommentAnchorPrefix(`${contextAnchorPrefix ?? ''}${selectedReviewAnchorPrefix}`)

  const {filePath, lineKey} = getThreadPreviewsLineAndFileKeysFromThread(thread)
  const storedThread = getThreadFromStore(Number(thread.id), filePath, lineKey)
  //TODO: if the stored thread is undefined, then add the thread to the store
  const isResolved = storedThread?.isResolved || thread.isResolved || false

  const [isCollapsed, setIsCollapsed] = useReviewThreadCollapseState(thread.id, isResolved)
  let diffLinesForPreview = thread.subject?.diffLines

  // if we don't have a start line, or it is the same as the end line, then we only want to show the diff line
  // the comment was made on.
  if (!thread.subject?.startLine || thread.subject?.startLine === thread.subject?.endLine) {
    diffLinesForPreview = thread.subject?.diffLines?.slice(-1)
  }

  // Memoize the expensive findIndex operation
  const selectedCommentIndex = useMemo(() => {
    const comments = thread.commentsData?.comments ?? []
    return displayComment ? comments.findIndex(c => c.id === displayComment.id) : 0
  }, [thread.commentsData?.comments, displayComment])

  const allComments = thread.commentsData?.comments ?? []
  const previousCommentCount = Math.max(selectedCommentIndex, 0)
  const replyCount = Math.max(allComments.length - previousCommentCount - 1, 0)
  const previousCommentId = allComments[previousCommentCount - 1]?.databaseId ?? null
  const nextCommentId = allComments[previousCommentCount + 1]?.databaseId ?? null

  const replyComposerRef = useRef<ThreadReplyComposerHandle>(null)

  // Route quote-reply from thread comments (whose built-in composer is disabled here) into the
  // external ThreadReplyComposer rendered below.
  const handleThreadQuoteReply = useCallback((quotedText: string | undefined) => {
    if (!quotedText) return
    replyComposerRef.current?.insertQuote(quotedText)
  }, [])

  // if the comment returned is a reply, this will be true
  if (!displayComment) return null

  return (
    <CommentLayout
      ref={containerRef}
      tabIndex={-1}
      flushBody /* for diff preview to fill layout */
      header={
        <ThreadHeader
          firstCommentId={displayComment?.databaseId}
          href={threadCommentHref}
          isCollapsed={isCollapsed}
          thread={thread}
          onNavigateToDiffComment={navigateToComment}
          onToggleCollapsed={() => setIsCollapsed(!isCollapsed)}
          headingLevel={headingLevel}
          isResolved={isResolved}
          isOutdated={isOutdated}
          lineKey={lineKey}
          resolutionReason={storedThread?.resolutionReason ?? thread.resolutionReason}
        />
      }
    >
      {!isCollapsed && (
        <>
          {diffLinesForPreview && diffLinesForPreview.length > 0 && (
            <div className="border-bottom borderColor-muted overflow-x-auto">
              <StaticUnifiedDiffPreview
                diffTableClassName="border-0"
                diffLines={diffLinesForPreview}
                className="m-0 border-0"
                tabSize={tabSize || 4}
              />
            </div>
          )}
          {selectedReviewId === null || isOwnedBySelectedReview ? (
            <>
              <ReviewThread
                getReactionContext={contextualCommentControls ? getReactionContext : undefined}
                lineMarkersKey={lineKey}
                batchingEnabled
                commentAnchorPrefix={anchorPrefix}
                disableSuggestedChangesActions
                filePath={filePath}
                gridCellId={`${thread.id}-comments-panel-thread`}
                isInlineComment={false}
                thread={thread}
                shouldLimitHeight={false}
                // built-in reply composer requires DiffStoreProvider to be in the tree, which is only true for files changed
                repliesDisabled
                onQuoteReply={handleThreadQuoteReply}
                onDelete={onDelete ? threadRemoved => onDelete(thread.id, threadRemoved) : undefined}
              />
              {thread.viewerCanReply && (
                <ThreadReplyComposer ref={replyComposerRef} thread={thread} startExpanded={false} />
              )}
            </>
          ) : (
            <>
              {previousCommentCount > 0 && previousCommentId !== null && (
                <div className="tmp-px-3 py-2 border-bottom borderColor-muted d-flex flex-items-center gap-2">
                  <PreviewAuthors thread={thread} skipAfterCommentId={displayComment?.databaseId} />
                  <Link
                    className="text-small fgColor-muted"
                    to={`#${threadCommentAnchor(String(previousCommentId), contextAnchorPrefix)}`}
                  >
                    {previousCommentCount === 1 ? '1 earlier comment' : `${previousCommentCount} earlier comments`}
                  </Link>
                </div>
              )}
              <div>
                <ReviewThreadComment
                  reactionContext={contextualCommentControls ? getReactionContext(displayComment) : undefined}
                  anchorPrefix={anchorPrefix}
                  lineMarkersKey={lineKey}
                  key={displayComment?.id}
                  hideActions
                  isInlineComment={false}
                  comment={displayComment}
                  filePath={filePath}
                  index={0}
                  isAnchorable={false}
                  isOutdated={isOutdated}
                  isThreadResolved={isResolved}
                  threadId={thread.id}
                  originalDiffPathUri={thread.originalDiffPathUri}
                  headingLevel="h3"
                  tooltipDirection="sw"
                />
              </div>
              {replyCount > 0 && nextCommentId !== null && (
                <div className="tmp-px-3 py-2 d-flex flex-items-center gap-2">
                  <PreviewAuthors thread={thread} skipBeforeCommentId={displayComment.databaseId} />
                  <Link
                    className="text-small fgColor-muted"
                    to={`#${threadCommentAnchor(String(nextCommentId), contextAnchorPrefix)}`}
                  >
                    {replyCount === 1 ? '1 later reply' : `${replyCount} later replies`}
                  </Link>
                </div>
              )}
            </>
          )}
        </>
      )}
    </CommentLayout>
  )
})

/**
 * Gets the comment to display for a thread based on the selected review ID.
 * If there's a review-id filter, returns the last comment in the thread that matches the review.
 * Otherwise, returns the first comment.
 */
function getDisplayComment(thread: Thread, selectedReviewId: number | null): Comment | undefined {
  const firstComment = getFirstComment(thread)

  // If no review-id filter, use first comment
  if (selectedReviewId === null) {
    return firstComment
  }

  // Find the last comment in the thread that matches the review ID
  const threadComments = thread.commentsData?.comments ?? []
  const matchingComments = threadComments.filter(comment => comment.reviewId === selectedReviewId)

  // Return the last matching comment, or fall back to first comment if no matches
  return matchingComments[matchingComments.length - 1] ?? firstComment
}
